import React, { useContext, useState, useEffect } from 'react';
import styled from 'astroturf/react';
import { CiterightContext, useUrlParam } from '../CiterightContextProvider';
import { Spinner } from 'js/design_system/Spinner';
import { Body } from 'js/design_system/Body';
import { Icon } from 'js/design_system/Icon';
import { TextButtonLink } from 'js/design_system/link/TextButtonLink';
import { DebugToggleButton } from './DebugToggleButton';
import { SaveReferencesButton } from './SaveReferencesButton';

const TopBarContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--background-white);
  border-bottom: 1px solid var(--stroke-light);
`;

const TopBarContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-xs-16);
  flex-wrap: wrap;
  padding: var(--spacing-xs-16) var(--spacing-sm-24);
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const LogoLink = styled.a`
  font-family:
    var(--type-font-family-serif, 'Georgia'), 'Times New Roman', serif;
  font-size: var(--type-serif-sm-size, 24px);
  line-height: var(--type-serif-sm-line-height, 32px);
  font-weight: var(--type-serif-sm-font-weight, 400);
  font-style: normal;

  &,
  &:hover,
  &:active,
  &:focus {
    text-decoration: none;
    color: var(--text-primary, #070a15);
  }

  transition:
    color 0.2s ease,
    border-color 0.2s ease;
  &:hover {
    opacity: 0.8;
    color: var(--hover, #4960d1);
    border-color: var(--hover, #4960d1);
  }
`;

const CenterSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-width: 0;
  gap: 40px;

  @media (max-width: 899px) {
    flex-basis: 100%;
    order: 1;
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs-16);
  flex-shrink: 0;
`;

const ActionsSection = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs-8);
  flex-shrink: 0;
`;

const NavLink = styled.button`
  background: none;
  border: none;
  padding: 8px 12px;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary, #070a15);
  display: flex;
  align-items: center;
  gap: 4px;
  border-radius: 4px;
  transition:
    background-color 0.2s ease,
    color 0.2s ease;

  &:hover {
    background-color: var(--background-beige-hover, rgba(0, 0, 0, 0.04));
    color: var(--brand-colors-blue-600, #4960d1);
  }

  &:active {
    background-color: var(--background-beige-active, rgba(0, 0, 0, 0.08));
  }
`;

const ActionSeparator = styled.div`
  width: 1px;
  height: 20px;
  background-color: var(--stroke-light, #dddde2);
  margin: 0 var(--spacing-xs-8);
`;

interface TopBarProps {
  page: 'triage' | 'debug' | 'overview';
  onToggle: (newPage: 'triage' | 'debug') => void;
  onNavigateToOverview?: () => void;
  children?: React.ReactNode;
}

export const TopBar = React.forwardRef<HTMLDivElement, TopBarProps>(
  ({ page, onToggle, onNavigateToOverview, children }, ref) => {
    const context = useContext(CiterightContext);
    const { value: ui } = useUrlParam('ui');
    const [fullSize, setFullSize] = useState(true);

    // Use ResizeObserver to detect width changes
    useEffect(() => {
      const element = typeof ref === 'function' ? null : ref?.current || null;
      if (!element) return;

      const checkWidth = () => {
        const width = element.offsetWidth;
        // Use 900px as breakpoint - buttons show text above this width
        setFullSize(width >= 900);
      };

      // Check initial width
      checkWidth();

      // Use ResizeObserver for efficient width monitoring
      const resizeObserver = new ResizeObserver(checkWidth);
      resizeObserver.observe(element);

      return () => {
        resizeObserver.disconnect();
      };
    }, [ref]);

    const getNewAnalysisUrl = () => {
      if (ui === 'inline') {
        // For inline UI, go to the landing page without any parameters (since inline is default)
        return '/citeright';
      } else if (ui !== null) {
        // For other ui values, preserve the parameter
        return `/citeright?ui=${ui}`;
      } else {
        // routes.citeright_path would be better, but it's not showing up in the routes.js file for some reason
        return '/citeright';
      }
    };

    const handleStartCitationAnalysis = () => {
      context?.startCitationReport();
    };

    return (
      <TopBarContainer ref={ref}>
        <TopBarContent>
          <LeftSection>
            {/* Keep this href logic in sync with handleNewAnalysis */}
            <LogoLink
              href={
                ui === 'inline' || ui === null
                  ? '/citeright'
                  : `/citeright?ui=${ui}`
              }
            >
              CiteRight
            </LogoLink>
          </LeftSection>

          <CenterSection>
            {context?.isPolling && !context?.report?.done && (
              <Spinner size={20} />
            )}
            {context?.report?.message_to_user && (
              <Body size="sm" style={{ color: 'var(--text-primary)' }}>
                {context.report.message_to_user}
              </Body>
            )}
          </CenterSection>

          <RightSection>
            <DebugToggleButton
              page={page}
              onToggle={onToggle}
              style={{ height: '100%' }}
            />
          </RightSection>

          <ActionsSection>
            {context?.report?.restartable && (
              <NavLink onClick={handleStartCitationAnalysis}>
                <Icon name="refresh" size={16} />
                {fullSize && 'Start Analysis'}
              </NavLink>
            )}
            {context?.report?.overview_ready && onNavigateToOverview && (
              <NavLink onClick={onNavigateToOverview}>
                <Icon name="description" size={16} />
                {fullSize && 'Key Claims'}
              </NavLink>
            )}
            <SaveReferencesButton fullSize={fullSize} />
            <ActionSeparator />
            <TextButtonLink
              href={getNewAnalysisUrl()}
              target="_blank"
              rel="noopener noreferrer"
              size="sm"
              icon={<Icon name="add" size={16} />}
              style={{ padding: '8px 12px', margin: 0 }}
              aria-label="Start a new citation analysis"
              trackClick={{
                source: 'citeright.new_analysis_clicked',
                entityId: context?.reportId || undefined,
              }}
            >
              {fullSize && 'New Analysis'}
            </TextButtonLink>
          </ActionsSection>
        </TopBarContent>
        {children}
      </TopBarContainer>
    );
  },
);

TopBar.displayName = 'TopBar';

export default TopBar;
